import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import { Settings } from 'types';

export const ALLOW_NON_EXPERT_MODE = process.env.REACT_APP_ENABLE_NON_EXPERT_MODE === 'true';

interface SettingsContextType {
  settings: Settings;
  updateSettings: (newSettings: Partial<Settings>) => void;
}

const defaultSettings: Settings = {
  expertMode: true,
  displayMode: 'system',
  showAnimations: true,
  colorBlindMode: false,
};

const SettingsContext = createContext<SettingsContextType | undefined>(undefined);

export const SettingsProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [settings, setSettings] = useState<Settings>(() => {
    const savedSettings = localStorage.getItem('settings');
    return savedSettings ? JSON.parse(savedSettings) : defaultSettings;
  });

  useEffect(() => {
    localStorage.setItem('settings', JSON.stringify(settings));

    // Apply display mode
    const systemDark = window.matchMedia('(prefers-color-scheme: dark)').matches;
    const effectivelyDark =
      settings.displayMode === 'dark' || (settings.displayMode === 'system' && systemDark);

    document.documentElement.classList.toggle('dark', effectivelyDark);
  }, [settings]);

  const updateSettings = useCallback((newSettings: Partial<Settings>) => {
    setSettings(prev => ({ ...prev, ...newSettings }));
  }, []);

  return (
    <SettingsContext.Provider value={{ settings, updateSettings }}>
      {children}
    </SettingsContext.Provider>
  );
};

export const useSettings = () => {
  const context = useContext(SettingsContext);
  if (context === undefined) {
    throw new Error('useSettings must be used within a SettingsProvider');
  }
  if (!ALLOW_NON_EXPERT_MODE) {
    context.settings.expertMode = true;
  }
  return context;
};
